import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, FileJson, Filter, MoreHorizontal, Plus, User, Workflow } from 'lucide-react';
import { useId, useMemo, useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { PageContainer } from '../app/AppLayout';
import { blueprintEtag, isApiError } from '../api/client';
import { useApi } from '../api/context';
import { queryKeys, useActiveRuns, useBlueprintPage, usePager } from '../api/queries';
import type { BlueprintDocument, BlueprintListItem, Diagnostic, RunKind } from '../api/types';
import { usePermission } from '../auth/context';
import { requiredRole } from '../auth/roles';
import { useToast } from '../components/toast';
import {
  ActionButton,
  Button,
  Dialog,
  DiagnosticList,
  ErrorMessage,
  Field,
  Loading,
  Pager,
} from '../components/ui';
import { describedBy } from '../lib/a11y';
import { emptyBlueprint } from '../lib/blueprint';
import {
  chipWord,
  matchesFilters,
  parseBlueprintFile,
  SUMMARY_CAP,
  versionsBehind,
  type DeploymentsFilter,
} from '../lib/blueprintList';
import { diagnosticsAt } from '../lib/diagnostics';
import { formatDateTime, formatRelative } from '../lib/format';
import { MenuButton, type MenuEntry } from '../shell/Menu';
import { StatusDot } from '../shell/Breadcrumbs';
import { ListActivityWindow } from '../runs/ActivityWindow';
import { strings } from '../strings';
import { useCardTable } from '../lib/cardTable';

const s = strings.blueprints;

const VISIBLE_CHIPS = 3;

function DeploymentChips({
  blueprint,
  activeKinds,
}: {
  blueprint: BlueprintListItem;
  activeKinds: ReadonlyMap<string, RunKind>;
}) {
  const { deployments } = blueprint;
  if (deployments.length === 0) return <span className="text-fg-muted">{s.notDeployed}</span>;
  const rest = deployments.length - VISIBLE_CHIPS;
  const capped = deployments.length >= SUMMARY_CAP;
  return (
    <ul className="chips" aria-label={s.deploymentChipsLabel(blueprint.name)}>
      {deployments.slice(0, VISIBLE_CHIPS).map((deployment) => {
        const word = chipWord(deployment, activeKinds);
        return (
          <li key={deployment.id}>
            <Link className="chip" to={`/deployments/${deployment.id}`}>
              <StatusDot status={deployment.status} />
              <span className="font-medium">{deployment.name}</span>
              {word && <span className="chip-word">{word}</span>}
            </Link>
          </li>
        );
      })}
      {rest > 0 && (
        <li>
          <Link
            className="chip chip-more"
            to={`/blueprints/${blueprint.id}/deployments`}
            aria-label={s.moreDeploymentsLabel(rest, capped)}
          >
            {s.moreDeployments(rest, capped)}
          </Link>
        </li>
      )}
    </ul>
  );
}

function downloadJson(name: string, content: unknown) {
  const blob = new Blob([JSON.stringify(content, null, 2) + '\n'], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}.json`;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function RowMenu({ blueprint, onDelete }: { blueprint: BlueprintListItem; onDelete: () => void }) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canDesign = usePermission('design');
  const roleReason = canDesign ? undefined : strings.common.requiresRole(requiredRole('design'));

  const duplicate = async () => {
    try {
      const source = await api.getBlueprint(blueprint.id);
      const created = await api.createBlueprint({
        ...source.data.content,
        name: s.copyName(source.data.name),
      });
      queryClient.setQueryData(queryKeys.blueprint(created.data.id), created);
      void queryClient.invalidateQueries({ queryKey: ['blueprints'] });
      navigate(`/blueprints/${created.data.id}`);
    } catch (error) {
      toast.error(error);
    }
  };

  const exportJson = async () => {
    try {
      const source = await api.getBlueprint(blueprint.id);
      downloadJson(source.data.name, source.data.content);
    } catch (error) {
      toast.error(error);
    }
  };

  const entries: MenuEntry[] = [
    { kind: 'link', id: 'deployments', label: s.deployments, to: `/blueprints/${blueprint.id}/deployments` },
    { kind: 'link', id: 'versions', label: s.versions, to: `/blueprints/${blueprint.id}/versions` },
    // Without `design` both stay, disabled with the role reason (spec 6.2).
    {
      kind: 'action',
      id: 'duplicate',
      label: s.duplicate,
      disabledReason: roleReason,
      onSelect: () => void duplicate(),
    },
    { kind: 'action', id: 'export', label: s.exportJson, onSelect: () => void exportJson() },
    { kind: 'separator', id: 'sep' },
    { kind: 'action', id: 'delete', label: s.delete, disabledReason: roleReason, onSelect: onDelete },
  ];
  return (
    <MenuButton
      // Portalled: the next rows' actions sit above this row, and the table clips its overflow.
      portal
      buttonClassName="btn btn-ghost btn-icon"
      buttonLabel={s.moreActions(blueprint.name)}
      buttonTitle={s.moreActions(blueprint.name)}
      buttonContent={<MoreHorizontal aria-hidden className="size-4" />}
      menuLabel={s.moreActions(blueprint.name)}
      entries={entries}
      placement="below-end"
    />
  );
}

function DeleteBlueprintDialog({
  blueprint,
  onClose,
}: {
  blueprint: BlueprintListItem;
  onClose: () => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteBlueprint(blueprint.id, blueprintEtag(blueprint.latestVersion));
      void queryClient.invalidateQueries({ queryKey: ['blueprints'] });
      onClose();
    } catch (error) {
      if (isApiError(error, 'BLUEPRINT_HAS_DEPLOYMENTS')) setBlocked(true);
      else toast.error(error);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={s.deleteTitle}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="danger" disabled={busy || blocked} onClick={() => void remove()} data-autofocus>
            {strings.common.delete}
          </Button>
        </>
      }
    >
      <p>{s.deleteConfirm(blueprint.name)}</p>
      {blocked && (
        <div role="alert" className="tone-danger mt-3 rounded-md border p-2 text-sm">
          <p className="font-semibold">{s.deleteBlocked}</p>
          <ul className="mt-1 list-disc pl-5">
            {blueprint.deployments.map((d) => (
              <li key={d.id}>
                <Link className="link" to={`/deployments/${d.id}`}>
                  {d.name}
                </Link>{' '}
                <span className="text-fg-muted">({strings.status.deployment[d.status]})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Dialog>
  );
}

function NewBlueprintDialog({ startWith, onClose }: { startWith: 'empty' | 'import'; onClose: () => void }) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const nameId = useId();
  const descriptionId = useId();
  const fileId = useId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState(startWith);
  const [imported, setImported] = useState<BlueprintDocument | null>(null);
  const [nameErrors, setNameErrors] = useState<string[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);
  const [busy, setBusy] = useState(false);

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setImported(null);
    setFileErrors([]);
    if (!file) return;
    const result = parseBlueprintFile(await file.text());
    if ('error' in result) {
      setFileErrors([result.error]);
      return;
    }
    setImported(result.document);
    if (!name.trim() && typeof result.document.name === 'string') setName(result.document.name);
    if (!description.trim() && typeof result.document.description === 'string') {
      setDescription(result.document.description);
    }
  };

  const submit = async () => {
    const trimmed = name.trim();
    setDiagnostics([]);
    if (!trimmed) return setNameErrors([s.nameRequired]);
    if (trimmed.length > 100) return setNameErrors([s.nameTooLong]);
    setNameErrors([]);
    if (start === 'import' && !imported)
      return setFileErrors(fileErrors.length ? fileErrors : [s.notBlueprint]);
    const base = start === 'import' && imported ? imported : emptyBlueprint(trimmed);
    const document: BlueprintDocument = { ...base, name: trimmed };
    if (description.trim()) document.description = description.trim();
    else delete document.description;
    setBusy(true);
    try {
      const created = await api.createBlueprint(document);
      queryClient.setQueryData(queryKeys.blueprint(created.data.id), created);
      void queryClient.invalidateQueries({ queryKey: ['blueprints'] });
      navigate(`/blueprints/${created.data.id}`);
    } catch (error) {
      if (isApiError(error) && error.diagnostics.length) {
        setNameErrors(diagnosticsAt(error.diagnostics, '/name').map((d) => d.message));
        setDiagnostics(error.diagnostics.filter((d) => d.path !== '/name'));
      } else {
        toast.error(error);
      }
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={s.createTitle}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {strings.common.create}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field label={s.name} htmlFor={nameId} help={s.nameHelp} errors={nameErrors} required>
          <input
            id={nameId}
            className="input"
            value={name}
            maxLength={100}
            aria-invalid={nameErrors.length ? true : undefined}
            aria-describedby={describedBy(nameId, true, nameErrors.length > 0)}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label={s.description} htmlFor={descriptionId} help={s.descriptionHelp}>
          <input
            id={descriptionId}
            className="input"
            value={description}
            aria-describedby={describedBy(descriptionId, true, false)}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <fieldset className="mb-3">
          <legend className="label">{s.startFrom}</legend>
          <div className="flex gap-4">
            {(['empty', 'import'] as const).map((value) => (
              <label key={value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="start-from"
                  value={value}
                  checked={start === value}
                  onChange={() => setStart(value)}
                />
                {value === 'empty' ? s.startEmpty : s.startImport}
              </label>
            ))}
          </div>
        </fieldset>
        {start === 'import' && (
          <Field label={s.file} htmlFor={fileId} errors={fileErrors}>
            <input
              id={fileId}
              type="file"
              accept=".json,application/json"
              className="input"
              aria-invalid={fileErrors.length ? true : undefined}
              aria-describedby={describedBy(fileId, false, fileErrors.length > 0)}
              onChange={(event) => void onFile(event)}
            />
          </Field>
        )}
        {diagnostics.length > 0 && (
          <div role="alert" className="tone-danger rounded-md border p-2">
            <DiagnosticList diagnostics={diagnostics} />
          </div>
        )}
      </form>
    </Dialog>
  );
}

function NewBlueprintButton({ onCreate }: { onCreate: (start: 'empty' | 'import') => void }) {
  const canDesign = usePermission('design');
  return (
    <div className="split-button">
      <ActionButton
        permission="design"
        variant="primary"
        icon={<Plus aria-hidden className="size-4" />}
        onClick={() => onCreate('empty')}
      >
        {s.newBlueprint}
      </ActionButton>
      {canDesign && (
        <MenuButton
          buttonClassName="btn btn-primary btn-icon"
          buttonLabel={s.moreCreate}
          buttonTitle={s.moreCreate}
          buttonContent={<ChevronDown aria-hidden className="size-4" />}
          menuLabel={s.moreCreate}
          placement="below-end"
          entries={[
            {
              kind: 'action',
              id: 'import',
              label: s.importJson,
              icon: <FileJson aria-hidden className="size-4" />,
              onSelect: () => onCreate('import'),
            },
          ]}
        />
      )}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: (start: 'empty' | 'import') => void }) {
  const canDesign = usePermission('design');
  return (
    <div className="empty-state">
      <span aria-hidden className="empty-state-icon">
        <Workflow className="size-6" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-[15px] font-semibold">{s.emptyTitle}</h2>
        <p className="mt-1 max-w-[620px] text-fg-muted">{s.emptyText}</p>
        {!canDesign && <p className="mt-1 text-fg-muted">{s.emptyReader}</p>}
      </div>
      {canDesign && (
        <div className="flex shrink-0 gap-2">
          <Button icon={<FileJson aria-hidden className="size-4" />} onClick={() => onCreate('import')}>
            {s.importJsonButton}
          </Button>
          <Button
            variant="primary"
            icon={<Plus aria-hidden className="size-4" />}
            onClick={() => onCreate('empty')}
          >
            {s.newBlueprint}
          </Button>
        </div>
      )}
    </div>
  );
}

/** `/blueprints` (spec 6.2, mock m10). */
export function BlueprintListPage() {
  const cardTable = useCardTable();
  const pager = usePager();
  const page = useBlueprintPage(pager.cursor);
  const activeRuns = useActiveRuns();
  const [creating, setCreating] = useState<'empty' | 'import' | null>(null);
  const [deleting, setDeleting] = useState<BlueprintListItem | null>(null);
  const [text, setText] = useState('');
  const [deployments, setDeployments] = useState<DeploymentsFilter>('any');
  const [updatedBy, setUpdatedBy] = useState('');
  const filterId = useId();
  const deploymentsId = useId();
  const updatedById = useId();

  const activeKinds = useMemo(
    () => new Map((activeRuns.data?.items ?? []).map((run) => [run.id, run.kind] as const)),
    [activeRuns.data],
  );
  const items = page.data?.items ?? [];
  const authors = [...new Set(items.map((b) => b.updatedBy))].sort((a, b) => a.localeCompare(b));
  const filtering = text.trim() !== '' || deployments !== 'any' || updatedBy !== '';
  const visible = items.filter((b) => matchesFilters(b, text, deployments, updatedBy));
  const empty = page.data && items.length === 0 && !pager.hasPrevious;

  return (
    <PageContainer>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">{s.title}</h1>
          <p className="mt-0.5 text-fg-muted">{s.subtitle}</p>
        </div>
        <NewBlueprintButton onCreate={setCreating} />
      </header>

      {!empty && (
        <div role="group" aria-label={strings.deployments.filters} className="list-toolbar">
          <div className="search-field">
            <label htmlFor={filterId} className="sr-only">
              {s.filter}
            </label>
            <input
              id={filterId}
              type="search"
              className="input"
              placeholder={s.filter}
              value={text}
              data-list-search
              aria-keyshortcuts="/"
              onChange={(e) => setText(e.target.value)}
            />
            <kbd aria-hidden className="kbd">
              /
            </kbd>
          </div>
          <div className="filter">
            <Filter aria-hidden className="size-3.5" />
            <label htmlFor={deploymentsId}>{s.deploymentsFilter}</label>
            <select
              id={deploymentsId}
              className="input"
              value={deployments}
              onChange={(e) => setDeployments(e.target.value as DeploymentsFilter)}
            >
              {(['any', 'deployed', 'notDeployed', 'problems'] as const).map((value) => (
                <option key={value} value={value}>
                  {s.deploymentsFilterValues[value]}
                </option>
              ))}
            </select>
          </div>
          <div className="filter">
            <User aria-hidden className="size-3.5" />
            <label htmlFor={updatedById}>{s.updatedByFilter}</label>
            <select
              id={updatedById}
              className="input"
              value={updatedBy}
              onChange={(e) => setUpdatedBy(e.target.value)}
            >
              <option value="">{s.anyone}</option>
              {authors.map((author) => (
                <option key={author} value={author}>
                  {author}
                </option>
              ))}
            </select>
          </div>
          <span className="ml-auto text-fg-muted">{s.order}</span>
        </div>
      )}
      {filtering && (
        <p role="status" className="help mb-2">
          {s.filteringNote}
        </p>
      )}

      {page.error ? (
        <ErrorMessage error={page.error} onRetry={() => void page.refetch()} />
      ) : !page.data ? (
        <Loading />
      ) : empty ? (
        <EmptyState onCreate={setCreating} />
      ) : visible.length === 0 ? (
        <p className="help">{s.noMatch}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table ref={cardTable} className="table table-cards blueprint-table">
            <thead>
              <tr>
                <th scope="col">{s.name}</th>
                <th scope="col">{s.deployments}</th>
                <th scope="col">{s.version}</th>
                <th scope="col">{s.updated}</th>
                <th scope="col">
                  <span className="sr-only">{s.actions}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((blueprint) => {
                const behind = versionsBehind(blueprint);
                return (
                  <tr key={blueprint.id} className="row-linked">
                    <td>
                      <span className="flex items-start gap-2.5">
                        <span aria-hidden className="icon-tile">
                          <Workflow className="size-4" />
                        </span>
                        <span className="min-w-0">
                          <Link className="row-link font-semibold" to={`/blueprints/${blueprint.id}`}>
                            {blueprint.name}
                          </Link>
                          <span className="block text-[12px] text-fg-muted">
                            {blueprint.description || strings.common.dash}
                          </span>
                        </span>
                      </span>
                    </td>
                    <td>
                      <DeploymentChips blueprint={blueprint} activeKinds={activeKinds} />
                    </td>
                    <td className="whitespace-nowrap">
                      v{blueprint.latestVersion}
                      {behind > 0 && (
                        <span className="badge tone-info ml-2" title={s.behindTitle}>
                          {s.behind(behind)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <span title={formatDateTime(blueprint.updatedAt)}>
                        {formatRelative(blueprint.updatedAt)}
                      </span>
                      <span className="block text-[12px] text-fg-muted">
                        {strings.common.by(blueprint.updatedBy)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <span className="row-actions">
                        <Link
                          className="btn"
                          to={`/blueprints/${blueprint.id}`}
                          aria-label={s.open(blueprint.name)}
                        >
                          {s.openButton}
                        </Link>
                        <RowMenu blueprint={blueprint} onDelete={() => setDeleting(blueprint)} />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pager
        hasPrevious={pager.hasPrevious}
        nextCursor={page.data?.nextCursor}
        onPrevious={pager.previous}
        onNext={pager.next}
      />
      {creating && <NewBlueprintDialog startWith={creating} onClose={() => setCreating(null)} />}
      {deleting && <DeleteBlueprintDialog blueprint={deleting} onClose={() => setDeleting(null)} />}
      <ListActivityWindow />
    </PageContainer>
  );
}
